import { Directive, ElementRef, Input, OnChanges, OnDestroy, inject } from '@angular/core';

const DURATION_MS = 700;

/**
 * Zählt eine Zahl sichtbar zum neuen Wert hoch (oder herunter), statt sie springen zu lassen.
 * Für Prozentwerte und Stimmenzahlen der Umfragen: Live-Zählerstände wirken so lebendig,
 * ohne dass bei jedem Update der ganze Block neu animiert.
 *
 *   <span [lseCountUp]="row.percent"></span>
 *   <span [lseCountUp]="average" [lseCountUpDecimals]="1" [lseCountUpLocale]="lang"></span>
 *
 * Bei „Bewegung reduzieren" steht die Zahl sofort.
 */
@Directive({
  selector: '[lseCountUp]',
  standalone: false
})
export class CountUpDirective implements OnChanges, OnDestroy {
  @Input('lseCountUp') value: number | null | undefined = 0;
  /** Nachkommastellen, z. B. 1 für einen Durchschnitt */
  @Input('lseCountUpDecimals') decimals = 0;
  /** Sprache für das Dezimaltrennzeichen (7,2 oder 7.2); ohne Angabe die des Browsers */
  @Input('lseCountUpLocale') locale: string | undefined;

  private readonly element = inject<ElementRef<HTMLElement>>(ElementRef);
  private shown = 0;
  private frame: number | null = null;

  ngOnChanges(): void {
    const target = this.round(this.value ?? 0);
    if (this.frame !== null) cancelAnimationFrame(this.frame);

    if (prefersReducedMotion() || target === this.shown) {
      this.render(target);
      return;
    }

    const from = this.shown;
    const start = performance.now();
    const step = (now: number) => {
      const progress = Math.min(1, (now - start) / DURATION_MS);
      const eased = 1 - Math.pow(1 - progress, 3);
      this.render(this.round(from + (target - from) * eased));
      this.frame = progress < 1 ? requestAnimationFrame(step) : null;
    };
    this.frame = requestAnimationFrame(step);
  }

  ngOnDestroy(): void {
    if (this.frame !== null) cancelAnimationFrame(this.frame);
  }

  private round(value: number): number {
    const factor = 10 ** this.decimals;
    return Math.round(value * factor) / factor;
  }

  private render(value: number): void {
    this.shown = value;
    this.element.nativeElement.textContent = this.decimals
      ? value.toLocaleString(this.locale, { minimumFractionDigits: this.decimals, maximumFractionDigits: this.decimals })
      : String(value);
  }
}

function prefersReducedMotion(): boolean {
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}
